/**
 * Modal di aggiunta/modifica di un candidato all'acquisto (SPEC-012.2 §4.1).
 *
 * Un solo componente per i due usi, perché è la stessa riga: si apre dalla
 * `WatchlistPage` con il titolo già scelto (creazione), oppure dalla
 * `WishlistPage` su una riga esistente (modifica).
 *
 * Due punti in cui la UI deve essere esplicita invece che comoda:
 *
 *  1. **Il portafoglio non è modificabile in modifica** (§3.1): cambiarlo
 *     trasformerebbe la riga in un'altra coppia. Il campo resta visibile ma
 *     disabilitato, con la ragione accanto — nasconderlo lascerebbe credere che
 *     la riga non abbia un portafoglio.
 *  2. **La valuta dell'esposizione è quella del portafoglio scelto**, non dello
 *     strumento, e si aggiorna insieme alla select: mostrare un importo con il
 *     simbolo sbagliato è il modo più diretto di far sbagliare una soglia.
 *  3. **Il ticker del broker si associa qui** (SPEC-014 R1), perché è qui che
 *     l'utente decide che un titolo è candidato all'acquisto — cioè l'ultimo
 *     momento utile prima che il motore provi a comprarlo e scopra di non
 *     sapere come si chiama presso il broker. La ricerca propone, l'utente
 *     conferma: l'associazione automatica è l'errore già pagato in F-048.
 *
 * L'associazione del ticker **non passa dal salvataggio di questo form**: è
 * un'anagrafica dello strumento, condivisa da tutte le righe che lo riguardano,
 * e ha un suo pulsante. Legarla al salvataggio la farebbe sembrare una proprietà
 * del candidato.
 */

import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { ApiError } from '@/lib/api-client';
import { BrokerTickerField } from '@/features/connectors/BrokerTickerField';
import { usePortfolios } from '@/features/portfolios/api';
import { Alert, Button, Field, Modal, Select, Textarea } from '@/ui';
import {
  useCreateWishlistItem,
  useUpdateWishlistItem,
  type WishlistItem,
} from './api';
import './wishlist.css';

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : fallback;
}

/** `YYYY-MM-DD` per un `<input type="date">`, senza slittamenti di fuso. */
function toDateInput(iso: string): string {
  return new Date(iso).toISOString().slice(0, 10);
}

function addDays(days: number): string {
  return new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

export type WishlistItemModalProps = {
  /** Creazione: il titolo arriva dalla riga di watchlist da cui si è aperto. */
  instrument?: { id: string; name: string; symbol?: string | null };
  /** Modifica: la riga esistente. Esclusivo con `instrument`. */
  item?: WishlistItem;
  defaultExpiryDays: number;
  onClose: () => void;
};

export function WishlistItemModal({ instrument, item, defaultExpiryDays, onClose }: WishlistItemModalProps) {
  const editing = item !== undefined;
  const portfolios = usePortfolios();
  const create = useCreateWishlistItem();
  const update = useUpdateWishlistItem();

  const [portfolioId, setPortfolioId] = useState(item?.portfolio.id ?? '');
  const [maxExposure, setMaxExposure] = useState(item?.maxExposureAmount ?? '');
  const [maxPercentage, setMaxPercentage] = useState(item?.maxPortfolioPercentage ?? '');
  const [expiresAt, setExpiresAt] = useState(item ? toDateInput(item.expiresAt) : '');
  const [internalNotes, setInternalNotes] = useState(item?.internalNotes ?? '');
  const [aiNotes, setAiNotes] = useState(item?.aiNotes ?? '');

  const options = useMemo(
    () =>
      (portfolios.data?.items ?? []).map((portfolio) => ({
        value: portfolio.id,
        label: `${portfolio.name} (${portfolio.baseCurrency})`,
      })),
    [portfolios.data],
  );

  const selectedCurrency = editing
    ? item.maxExposureCurrency
    : (portfolios.data?.items ?? []).find((portfolio) => portfolio.id === portfolioId)?.baseCurrency;

  const title = editing
    ? `Modifica candidato — ${item.instrument.name}`
    : `Aggiungi a wishlist — ${instrument?.name ?? ''}`;

  const pending = create.isPending || update.isPending;
  const failure = create.error ?? update.error;

  /**
   * Soglia del selettore di data. Per una scadenza **nuova** e' domani: una
   * scadenza scelta per oggi nascerebbe gia' passata.
   *
   * Su una riga esistente il valore gia' salvato e' un **fatto**, non una
   * proposta, e va ammesso anche se e' nel passato. Senza questa deroga il
   * browser dichiarava il campo non valido e — **senza dire niente** —
   * rifiutava l'invio del form: il pulsante «Salva» non faceva nulla, e la
   * riga scaduta diventava impossibile da modificare. Proprio quelle righe la
   * wishlist le conserva apposta (scadere significa sparire dagli elenchi, non
   * essere cancellata), quindi il caso non e' un residuo ma il normale
   * appuntamento con il rinnovo.
   *
   * La soglia si calcola sul valore **salvato**, non su quello del form: se
   * seguisse lo stato corrente, chi apre una riga scaduta potrebbe poi
   * spostarla ancora piu' indietro.
   */
  const savedExpiry = item ? toDateInput(item.expiresAt) : '';
  const tomorrow = addDays(1);
  const minExpiry = savedExpiry !== '' && savedExpiry < tomorrow ? savedExpiry : tomorrow;

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    // Campo vuoto = "nessun vincolo". In modifica va inviato `null` esplicito
    // per rimuoverlo: `undefined` significherebbe "lascia com'è" e il vincolo
    // resterebbe, con l'utente convinto di averlo cancellato.
    // Campo vuoto = `null` esplicito anche per le note: `undefined` significa
    // "lascia com'è", e su `aiNotes` vorrebbe dire che un'istruzione che l'utente
    // crede di aver cancellato continua a raggiungere il modello.
    const internal = internalNotes.trim() === '' ? null : internalNotes.trim();
    const forAi = aiNotes.trim() === '' ? null : aiNotes.trim();
    const exposure = maxExposure.trim() === '' ? null : maxExposure.trim();
    const percentage = maxPercentage.trim() === '' ? null : maxPercentage.trim();
    // Un `<input type="date">` dà solo il giorno: si fissa la fine di quel
    // giorno, altrimenti una scadenza scelta per "oggi" nascerebbe già passata.
    const expiry = expiresAt === '' ? undefined : new Date(`${expiresAt}T23:59:59.000Z`).toISOString();

    if (editing) {
      update.mutate(
        {
          id: item.id,
          maxExposureAmount: exposure,
          maxPortfolioPercentage: percentage,
          internalNotes: internal,
          aiNotes: forAi,
          ...(expiry ? { expiresAt: expiry } : {}),
        },
        { onSuccess: onClose },
      );
      return;
    }

    if (!instrument || portfolioId === '') return;
    create.mutate(
      {
        instrumentId: instrument.id,
        portfolioId,
        maxExposureAmount: exposure,
        maxPortfolioPercentage: percentage,
        internalNotes: internal,
        aiNotes: forAi,
        ...(expiry ? { expiresAt: expiry } : {}),
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Modal title={title} onClose={onClose}>
      <form className="wishlist__form" onSubmit={handleSubmit}>
        <Select
          label="Portafoglio"
          value={editing ? item.portfolio.id : portfolioId}
          onChange={(e) => setPortfolioId(e.target.value)}
          options={editing ? [{ value: item.portfolio.id, label: item.portfolio.name }] : options}
          placeholder={editing ? undefined : 'Scegli un portafoglio…'}
          disabled={editing}
          hint={
            editing
              ? 'Non modificabile: per spostare il titolo su un altro portafoglio, elimina questa riga e ricreala.'
              : 'Lo stesso titolo può essere candidato su più portafogli, con vincoli indipendenti.'
          }
          required={!editing}
        />

        <Field
          label={`Esposizione massima${selectedCurrency ? ` (${selectedCurrency})` : ''}`}
          value={maxExposure}
          onChange={(e) => setMaxExposure(e.target.value)}
          numeric
          hint={
            selectedCurrency
              ? `Quanto sei disposto a investire su questo titolo in questo portafoglio, in ${selectedCurrency}. Vuoto = nessun limite.`
              : 'Scegli prima un portafoglio: la valuta è quella del portafoglio. Vuoto = nessun limite.'
          }
        />

        <Field
          label="Peso massimo sul portafoglio (%)"
          value={maxPercentage}
          onChange={(e) => setMaxPercentage(e.target.value)}
          numeric
          hint="Da 0 a 100, calcolato sul valore dell'ultimo snapshot del portafoglio. Vuoto = nessun limite."
        />

        <Field
          label="Scadenza"
          type="date"
          value={expiresAt}
          onChange={(e) => setExpiresAt(e.target.value)}
          min={minExpiry}
          hint={
            expiresAt === ''
              ? `Se lasci vuoto: ${defaultExpiryDays} giorni da oggi (${addDays(defaultExpiryDays)}).`
              : 'Passata la scadenza la riga non compare più negli elenchi, ma non viene cancellata.'
          }
        />

        <Textarea
          label="Note interne"
          value={internalNotes}
          onChange={(event) => setInternalNotes(event.target.value)}
          rows={3}
          hint="Solo per te. Non vengono mai inviate al modello."
        />

        <Textarea
          label="Note per l'analisi AI"
          value={aiNotes}
          onChange={(event) => setAiNotes(event.target.value)}
          rows={3}
          hint="Vengono lette dal modello quando l'auto-trading valuta questo titolo. Esempio: «non comprare prima della trimestrale»."
        />

        <BrokerTickerField
          instrumentId={editing ? item.instrument.id : (instrument?.id ?? '')}
          instrumentName={editing ? item.instrument.name : (instrument?.name ?? '')}
          currentTicker={editing ? item.instrument.trading212Ticker : null}
          defaultQuery={editing ? (item.instrument.symbol ?? item.instrument.name) : (instrument?.symbol ?? instrument?.name ?? '')}
        />

        {failure && (
          <Alert variant="error" title={editing ? 'Modifica non riuscita' : 'Candidato non aggiunto'}>
            {errorMessage(failure, 'Errore imprevisto')}
          </Alert>
        )}

        <div className="wishlist__form-actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button type="submit" disabled={pending || (!editing && portfolioId === '')}>
            {pending ? 'Salvataggio…' : editing ? 'Salva' : 'Aggiungi a wishlist'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
